import type { FC } from "react";

import type { OnboardingStep } from "../../hooks/useOnboarding";

const STEP_LABELS: Record<OnboardingStep, string> = {
  "create-org": "Organization",
  done: "Done",
  github: "GitHub",
  jira: "Jira",
  payment: "Billing",
  "pending-invites": "Invites",
};

function barColor(isComplete: boolean, isActive: boolean): string {
  if (isComplete) {
    return "bg-emerald-500";
  }
  if (isActive) {
    return "bg-emerald-500/40";
  }
  return "bg-slate-700";
}

function glyphFor(isComplete: boolean, isActive: boolean): string {
  if (isComplete) {
    return "■";
  }
  if (isActive) {
    return "□";
  }
  return "·";
}

function glyphColor(isComplete: boolean, isActive: boolean): string {
  if (isComplete || isActive) {
    return "text-emerald-400";
  }
  return "text-slate-600";
}

function labelColor(isComplete: boolean, isActive: boolean): string {
  if (isComplete) {
    return "text-emerald-400";
  }
  if (isActive) {
    return "text-white";
  }
  return "text-slate-600";
}

interface OnboardingStepperProps {
  steps: OnboardingStep[];
  currentStep: OnboardingStep;
  completedSteps: Set<OnboardingStep>;
}

export const OnboardingStepper: FC<OnboardingStepperProps> = ({
  steps,
  currentStep,
  completedSteps,
}) => {
  const currentIndex = steps.indexOf(currentStep);

  return (
    <div
      className="w-full space-y-3"
      role="progressbar"
      aria-valuenow={currentIndex + 1}
      aria-valuemin={1}
      aria-valuemax={steps.length}
      aria-label="Onboarding progress"
    >
      {/* One column per step: bar segment with its label stacked beneath, so
          they can never drift out of alignment. */}
      <div className="flex gap-1">
        {steps.map((step, index) => {
          const isComplete = completedSteps.has(step);
          const isActive = step === currentStep;

          return (
            <div
              key={step}
              className="flex min-w-0 flex-1 flex-col gap-2"
              aria-current={isActive ? "step" : undefined}
            >
              <div
                className={`h-1 w-full transition-all duration-300 ${barColor(isComplete, isActive)}`}
              />
              <div className="flex items-center justify-center gap-1">
                <span
                  className={`font-mono text-[8px] ${glyphColor(isComplete, isActive)}`}
                >
                  {glyphFor(isComplete, isActive)}
                </span>
                <span
                  className={`font-mono text-[10px] ${glyphColor(isComplete, isActive)}`}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>
              <span
                className={`truncate text-center font-mono text-[9px] font-bold uppercase tracking-wider transition-colors ${labelColor(isComplete, isActive)}`}
                title={STEP_LABELS[step]}
              >
                {STEP_LABELS[step]}
              </span>
            </div>
          );
        })}
      </div>

      {/* Visually hidden live region for screen readers */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        Step {currentIndex + 1} of {steps.length}: {STEP_LABELS[currentStep]}
      </div>
    </div>
  );
};
